/**
 * Profile Awards & Achievements Page
 * Add and manage awards, achievements, and recognitions
 * Uses awards store and artist API with year/genre selectors
 *
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Trophy, Medal, ArrowLeft, Plus, X, Award, Gift, Calendar, Star } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import type { ArtistAwardApiItem } from '@/services/artistService';
import { useAwardsStore } from '@/stores/awardsStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { Loader } from '@/components/ui';
import {
  ProfileCompletionBlocksControl,
  ProfileIncompleteCornerPing,
} from '@/components/profile/ProfileCompletionBlocksControl';
import AchievmentRoad from './website-preview/Elite/components/AchievmentRoad';
import AchievmentMobileRoad from './website-preview/Elite/components/AchievmentMobileRoad';
import ArtistPremiumTrophy from './website-preview/Premium/components/ArtistPremiumTrophy';
import ArtistEconomyTrophy from './website-preview/Economy/components/ArtistEconomyTrophy';
import ArtistFreeAwards from './website-preview/Free/components/ArtistFreeAwards';

type PlanKey = 'free' | 'economy' | 'premium' | 'elite';

function subscriptionNameToPlanKey(subscriptionName: string | null | undefined): PlanKey | null {
  if (!subscriptionName) return null;
  const n = subscriptionName.toLowerCase().trim();
  if (!n) return null;
  if (n.includes('limited')) return 'free';
  if (n.includes('elite')) return 'elite';
  if (n.includes('premium')) return 'premium';
  if (n.includes('economy')) return 'economy';
  if (n.includes('eco')) return 'economy';
  if (n.includes('free')) return 'free';
  return null;
}

const currentYear = new Date().getFullYear();
const YEAR_START = 1850;
const YEAR_OPTIONS: SelectOption[] = Array.from(
  { length: currentYear - YEAR_START + 1 },
  (_, i) => {
    const y = currentYear - i;
    return { value: String(y), label: String(y) };
  },
);

const AWARD_NAME_MAX_LEN = 20;
const AWARD_FOR_MAX_LEN = 100;
const ACHIEVEMENT_NAME_MAX_LEN = 20;

interface AwardEntry {
  id: string;
  backendId?: number;
  name: string;
  for: string;
  year: string;
}

interface AchievementEntry {
  id: string;
  backendId?: number;
  name: string;
  year: string;
}

function mapStoreDataToAwards(items: ArtistAwardApiItem[] | null): AwardEntry[] {
  if (!items?.length) return [];
  return items
    .filter((item) => item.type === 'award')
    .map((item) => ({
      id: `award-${item.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`}`,
      backendId: item.id,
      name: item.name ?? '',
      for: item.description ?? '',
      year: item.date ? String(item.date).slice(0, 4) : '',
    }));
}

function mapStoreDataToAchievements(items: ArtistAwardApiItem[] | null): AchievementEntry[] {
  if (!items?.length) return [];
  return items
    .filter((item) => item.type === 'achievement')
    .map((item) => ({
      id: `ach-${item.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`}`,
      backendId: item.id,
      name: item.name ?? '',
      year: item.date ? String(item.date).slice(0, 4) : '',
    }));
}

export const ProfileAwardsAchievementsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeData = useAwardsStore((s) => s.data);
  const isLoading = useAwardsStore((s) => s.isLoading);
  const ensureFetched = useAwardsStore((s) => s.ensureFetched);
  const addItems = useAwardsStore((s) => s.addItems);
  const deleteItem = useAwardsStore((s) => s.deleteItem);
  const setStoreError = useAwardsStore((s) => s.setError);

  const [awards, setAwards] = useState<AwardEntry[]>([]);
  const [achievements, setAchievements] = useState<AchievementEntry[]>([]);
  const [awardErrors, setAwardErrors] = useState<Record<string, string>>({});
  const [achievementErrors, setAchievementErrors] = useState<Record<string, string>>({});
  const [saveAwardsLoading, setSaveAwardsLoading] = useState(false);
  const [saveAchievementsLoading, setSaveAchievementsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const awardInputRefs = useRef<Record<string, HTMLInputElement | HTMLButtonElement | null>>({});
  const achievementInputRefs = useRef<Record<string, HTMLInputElement | HTMLButtonElement | null>>({});
  const [isEditingAwards, setIsEditingAwards] = useState(false);
  const [isEditingAchievements, setIsEditingAchievements] = useState(false);
  const subscriptionName = useArtistDetailsStore(
    (s) => s.data?.subscriptions.subscription_name,
  );
  const incompletedSections = useArtistDetailsStore((s) => s.data?.incompleted_sections);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);

  const { awardsNeedAttention, achievementsNeedAttention } = useMemo(() => {
    const raw = incompletedSections;
    if (!Array.isArray(raw)) {
      return { awardsNeedAttention: false, achievementsNeedAttention: false };
    }
    const set = new Set(raw);
    return {
      awardsNeedAttention: set.has('awards'),
      achievementsNeedAttention: set.has('achievements'),
    };
  }, [incompletedSections]);
  const planParam = (searchParams.get('plan') ?? '').toLowerCase() as PlanKey | '';
  const subscriptionPlan = useMemo(
    () => subscriptionNameToPlanKey(subscriptionName),
    [subscriptionName],
  );
  const plan: PlanKey = (planParam && ['free', 'economy', 'premium', 'elite'].includes(planParam)
    ? planParam
    : subscriptionPlan ?? 'free') as PlanKey;
  const planPreviewLabel = plan === 'free' ? 'LIMITED' : plan;

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  useEffect(() => {
    ensureArtistDetailsFetched().catch(() => undefined);
  }, [ensureArtistDetailsFetched]);

  useEffect(() => {
    setAwards(mapStoreDataToAwards(storeData));
    setAchievements(mapStoreDataToAchievements(storeData));
  }, [storeData]);

  const addAward = () => {
    setIsEditingAwards(true);
    setAwards((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', for: '', year: '' },
    ]);
  };

  const removeAward = async (id: string) => {
    const award = awards.find((a) => a.id === id);
    if (award?.backendId != null) {
      setDeletingId(id);
      try {
        await deleteItem(award.backendId);
        toast.success('Award removed');
      } catch {
        // Store sets error; useEffect toasts it
      } finally {
        setDeletingId(null);
      }
    } else {
      setAwards((prev) => prev.filter((a) => a.id !== id));
    }
  };

  const addAchievement = () => {
    setIsEditingAchievements(true);
    setAchievements((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', year: '' },
    ]);
  };

  const removeAchievement = async (id: string) => {
    const ach = achievements.find((a) => a.id === id);
    if (ach?.backendId != null) {
      setDeletingId(id);
      try {
        await deleteItem(ach.backendId);
        toast.success('Achievement removed');
      } catch {
        // Store sets error; useEffect toasts it
      } finally {
        setDeletingId(null);
      }
    } else {
      setAchievements((prev) => prev.filter((a) => a.id !== id));
    }
  };

  const cancelAwards = () => {
    setAwards((prev) => prev.filter((a) => a.backendId != null));
    setAwardErrors({});
    setIsEditingAwards(false);
  };

  const cancelAchievements = () => {
    setAchievements((prev) => prev.filter((a) => a.backendId != null));
    setAchievementErrors({});
    setIsEditingAchievements(false);
  };

  const buildApiItemFromAward = (award: AwardEntry): ArtistAwardApiItem => ({
    id: award.backendId,
    type: 'award',
    name: award.name,
    description: award.for || null,
    date: award.year ? `${award.year}-01-01` : null,
    organization: null,
  });

  const buildApiItemFromAchievement = (ach: AchievementEntry): ArtistAwardApiItem => ({
    id: ach.backendId,
    type: 'achievement',
    name: ach.name,
    description: null,
    date: ach.year ? `${ach.year}-01-01` : null,
    organization: null,
  });

  const storeError = useAwardsStore((s) => s.error);
  useEffect(() => {
    if (storeError) {
      toast.error(storeError);
      setStoreError(null);
    }
  }, [storeError, setStoreError]);

  const handleSaveAwards = async () => {
    const newErrors: Record<string, string> = {};
    const newAwards = awards.filter((a) => a.backendId == null);
    newAwards.forEach((award) => {
      const base = `award-${award.id}`;
      if (!award.name) newErrors[`${base}-name`] = 'Award name is required';
      else if (award.name.length > AWARD_NAME_MAX_LEN) {
        newErrors[`${base}-name`] = `Award name must be ${AWARD_NAME_MAX_LEN} characters or less`;
      }
      if (!award.for) newErrors[`${base}-for`] = 'Awarded for is required';
      else if (award.for.length > AWARD_FOR_MAX_LEN) {
        newErrors[`${base}-for`] = `Awarded for must be ${AWARD_FOR_MAX_LEN} characters or less`;
      }
      if (!award.year) newErrors[`${base}-year`] = 'Year received is required';
    });

    setAwardErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      toast.error(newErrors[firstKey]);
      awardInputRefs.current[firstKey]?.focus();
      return;
    }

    try {
      setSaveAwardsLoading(true);
      const payload = awards
        .filter((a) => a.backendId == null)
        .map(buildApiItemFromAward);
      await addItems(payload);
      setIsEditingAwards(false);
    } catch {
      toast.error('Failed to save awards.');
    } finally {
      setSaveAwardsLoading(false);
    }
  };

  const handleSaveAchievements = async () => {
    const newErrors: Record<string, string> = {};
    const newAchievements = achievements.filter((a) => a.backendId == null);
    newAchievements.forEach((ach) => {
      const base = `ach-${ach.id}`;
      if (!ach.name) newErrors[`${base}-name`] = 'Achievement name is required';
      else if (ach.name.length > ACHIEVEMENT_NAME_MAX_LEN) {
        newErrors[`${base}-name`] = `Achievement name must be ${ACHIEVEMENT_NAME_MAX_LEN} characters or less`;
      }
      if (!ach.year) newErrors[`${base}-year`] = 'Year Recieved is required';
    });

    setAchievementErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      toast.error(newErrors[firstKey]);
      achievementInputRefs.current[firstKey]?.focus();
      return;
    }

    try {
      setSaveAchievementsLoading(true);
      const payload = achievements
        .filter((a) => a.backendId == null)
        .map(buildApiItemFromAchievement);
      await addItems(payload);
      setIsEditingAchievements(false);
    } catch {
      toast.error('Failed to save achievements.');
    } finally {
      setSaveAchievementsLoading(false);
    }
  };

  return (
    <div className="w-full  bg-[#0B0B0D] text-white">
      <div className="w-full  mx-auto md:px-4 md:py-6 lg:py-8 pb-24">
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
                <Trophy className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                  Awards & Achievements
                </h1>
                <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                  Highlight your key recognitions to build trust
                </p>
              </div>
            </div>
          </div>
        </header>
        {!isEditingAwards && !isEditingAchievements && (
          <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4] flex items-center justify-between gap-3">
            <span>
              This is a live preview of how your Awards & Achievements section will appear on your public website.
            </span>
            <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#E5E5EA]">
              Plan: {planPreviewLabel}
            </span>
          </div>
        )}
        <div className="flex items-end justify-end gap-2 flex-wrap mb-6">
          {/* Default state: both add buttons */}
          {!isEditingAwards && !isEditingAchievements && (
            <>
              <span className="relative inline-flex shrink-0">
                <button
                  type="button"
                  onClick={addAward}
                  className="relative z-0 inline-flex items-center gap-1.5 rounded-full border border-[#3A3A3C] bg-[#151517] px-3 py-1.5 text-[11px] text-[#F2F2F7] text-nowrap transition-colors hover:bg-[#2C2C2E]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Awards
                </button>
                {awardsNeedAttention ? (
                  <ProfileIncompleteCornerPing className="right-0 top-0 z-[2] md:right-0 md:top-0" />
                ) : null}
              </span>
              <span className="relative inline-flex shrink-0">
                <button
                  type="button"
                  onClick={addAchievement}
                  className="relative z-0 inline-flex items-center gap-1.5 rounded-full border border-[#3A3A3C] bg-[#151517] px-3 py-1.5 text-[11px] text-[#F2F2F7] text-nowrap transition-colors hover:bg-[#2C2C2E]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Achievements
                </button>
                {achievementsNeedAttention ? (
                  <ProfileIncompleteCornerPing className="right-0 top-0 z-[2] md:right-0 md:top-0" />
                ) : null}
              </span>
            </>
          )}



          {/* Editing awards: show Add award + Save + Cancel in one row */}
          {isEditingAwards && (
            <div className="flex flex-row justify-between items-center gap-2 w-full">
              <button
                type="button"
                onClick={addAward}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
              >
                <Plus className="h-3.5 w-3.5" />
                Add more
              </button>
              <div className="flex flex-row justify-end items-center gap-2">
                {awards.some((a) => a.backendId == null) && (
                  <>
                    <button
                      type="button"
                      onClick={handleSaveAwards}
                      disabled={saveAwardsLoading || isLoading}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#0B0B0D] hover:bg-[#2C2C2E] hover:text-white transition-colors text-nowrap disabled:opacity-50"
                    >
                      {saveAwardsLoading ? <Loader size="small" /> : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelAwards}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>

            </div>
          )}

          {/* Editing achievements: show Add achievement + Save + Cancel in one row */}
          {isEditingAchievements && (
            <div className="flex flex-row justify-between items-center gap-2 w-full">
              <button
                type="button"
                onClick={addAchievement}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
              >
                <Plus className="h-3.5 w-3.5" />
                Add more
              </button>
              <div className="flex flex-row justify-end items-center gap-2">
                {achievements.some((a) => a.backendId == null) && (
                  <>
                    <button
                      type="button"
                      onClick={handleSaveAchievements}
                      disabled={saveAchievementsLoading || isLoading}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#0B0B0D] hover:bg-[#2C2C2E] hover:text-white transition-colors text-nowrap disabled:opacity-50"
                    >
                      {saveAchievementsLoading ? <Loader size="small" /> : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelAchievements}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="space-y-6 lg:space-y-8"
        >
          {/* Awards */}
          <section className="rounded-2xl space-y-4">
            {!isEditingAwards && !isEditingAchievements && (
              <div className="rounded-2xl px-4 py-6 lg:px-6 lg:py-8">
                {!isLoading && awards.length === 0 && achievements.length === 0 && (
                  <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4]">
                    Your Awards & Achievements are not added yet. Please add them using the <span className="font-medium text-[#E5E5EA]">Add Awards</span> and <span className="font-medium text-[#E5E5EA]">Add Achievements</span> buttons.
                  </div>
                )}
                <AnimatePresence mode="wait">
                  {plan === 'elite' && (
                    <motion.div
                      key="elite"
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      <div className="hidden md:block mt-48 -mx-4">
                        <AchievmentRoad enableDelete />
                      </div>
                      <div className="md:hidden mt-32 -mx-4">
                        <AchievmentMobileRoad enableDelete />
                      </div>
                    </motion.div>
                  )}

                  {plan === 'premium' && (
                    <motion.div
                      key="premium"
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      <div className="-mt-20 -mx-4">
                        <ArtistPremiumTrophy enableDelete />
                      </div>
                    </motion.div>
                  )}

                  {plan === 'economy' && (
                    <motion.div
                      key="economy"
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      <div className="-mt-20 -mx-4">
                        <ArtistEconomyTrophy enableDelete />
                      </div>
                    </motion.div>
                  )}

                  {plan === 'free' && (
                    <motion.div
                      key="free"
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                      className="-mt-20 md:-mt-32"
                    >
                      <ArtistFreeAwards hideTitle enableDelete />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            {isEditingAwards && (
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Awards
                </h2>
              </div>
            )}
            {isLoading && awards.length === 0 ? (
              <div className="flex items-center justify-center min-h-[80px]">
                <Loader size='small' />
              </div>
            ) : (
              <>
                {isEditingAwards && awards.some((a) => a.backendId == null) ? (
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pb-10">
                    {awards
                      .filter((a) => a.backendId == null)
                      .map((award, index) => {
                        const base = `award-${award.id}`;
                        return (
                          <div
                            key={award.id}
                            className="rounded-2xl border border-[#3A3A3C]/50 px-4 py-4 space-y-3 relative bg-[linear-gradient(135deg,_#0a0a0c_0%,_#121215_50%,_#16161a_100%)]"
                          >
                            <p className="text-xs font-medium text-[#8E8E93]">#{index + 1}</p>
                            <div className="space-y-3">
                              <div>
                                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                  <Award className="h-4 w-4 shrink-0" />
                                  Award name
                                </label>
                                <Input
                                  ref={(el) => { awardInputRefs.current[`${base}-name`] = el; }}
                                  value={award.name}
                                  onChange={(e) =>
                                    setAwards((prev) =>
                                      prev.map((a) =>
                                        a.id === award.id ? { ...a, name: e.target.value } : a,
                                      ),
                                    )
                                  }
                                  placeholder="e.g. Best New Artist"
                                  maxLength={AWARD_NAME_MAX_LEN}
                                  className="bg-transparent border border-[#3A3A3C]"
                                  error={awardErrors[`${base}-name`]}
                                  hideErrorText
                                />
                              </div>
                              <div>
                                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                  <Gift className="h-4 w-4 shrink-0" />
                                  Awarded for
                                </label>
                                <Input
                                  ref={(el) => { awardInputRefs.current[`${base}-for`] = el; }}
                                  value={award.for}
                                  onChange={(e) =>
                                    setAwards((prev) =>
                                      prev.map((a) =>
                                        a.id === award.id ? { ...a, for: e.target.value } : a,
                                      ),
                                    )
                                  }
                                  placeholder="e.g. Outstanding live set at Summer Fest"
                                  maxLength={AWARD_FOR_MAX_LEN}
                                  className="bg-transparent border border-[#3A3A3C]"
                                  error={awardErrors[`${base}-for`]}
                                  hideErrorText
                                />
                              </div>
                              <div>
                                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                  <Calendar className="h-4 w-4 shrink-0" />
                                  Year received
                                </label>
                                <Select
                                  ref={(el) => { awardInputRefs.current[`${base}-year`] = el; }}
                                  options={YEAR_OPTIONS}
                                  value={award.year || undefined}
                                  onChange={(value) =>
                                    setAwards((prev) =>
                                      prev.map((a) =>
                                        a.id === award.id ? { ...a, year: value } : a,
                                      ),
                                    )
                                  }
                                  placeholder="Select year"
                                  className=""
                                  error={awardErrors[`${base}-year`]}
                                  hideErrorText
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : null}
              </>
            )}
          </section>

          {/* Achievements */}
          <section className="rounded-2xl space-y-4">
            {isEditingAchievements && (
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                  <Medal className="h-5 w-5 text-emerald-500" />
                  Achievements
                </h2>
              </div>
            )}
            {isLoading && achievements.length === 0 ? (
              <div className="flex items-center justify-center min-h-[80px]">
                <Loader size='small' />
              </div>
            ) : (
              <>
                {isEditingAchievements && achievements.some((a) => a.backendId == null) ? (
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {achievements
                      .filter((a) => a.backendId == null)
                      .map((ach, index) => {
                        const base = `ach-${ach.id}`;
                        return (
                          <div
                            key={ach.id}
                            className="rounded-2xl border border-[#3A3A3C]/50 px-4 py-4 space-y-3 relative bg-[linear-gradient(135deg,_#0a0a0c_0%,_#121215_50%,_#16161a_100%)]"
                          >
                            <p className="text-xs font-medium text-[#8E8E93]">
                              #{index + 1}
                            </p>
                            <div className="grid gap-3 md:grid-cols-1">
                              <div>
                                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                  <Star className="h-4 w-4 shrink-0" />
                                  Achievement name
                                </label>
                                <Input
                                  ref={(el) => { achievementInputRefs.current[`${base}-name`] = el; }}
                                  value={ach.name}
                                  onChange={(e) =>
                                    setAchievements((prev) =>
                                      prev.map((a) =>
                                        a.id === ach.id ? { ...a, name: e.target.value } : a,
                                      ),
                                    )
                                  }
                                  placeholder="e.g. 1M monthly listeners on Spotify"
                                  maxLength={ACHIEVEMENT_NAME_MAX_LEN}
                                  className="bg-transparent border border-[#3A3A3C]"
                                  error={achievementErrors[`${base}-name`]}
                                  hideErrorText
                                />
                              </div>
                              <div>
                                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#8E8E93]">
                                  <Calendar className="h-4 w-4 shrink-0" />
                                  Year received
                                </label>
                                <Select
                                  ref={(el) => { achievementInputRefs.current[`${base}-year`] = el; }}
                                  options={YEAR_OPTIONS}
                                  value={ach.year || undefined}
                                  onChange={(value) =>
                                    setAchievements((prev) =>
                                      prev.map((a) =>
                                        a.id === ach.id ? { ...a, year: value } : a,
                                      ),
                                    )
                                  }
                                  placeholder="Select year"
                                  className=""
                                  error={achievementErrors[`${base}-year`]}
                                  hideErrorText
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : null}
              </>
            )}

          </section>
        </motion.form>
      </div>
    </div >
  );
};

